import { useState } from "react";
import { askQuestion } from "./ask.service";
import { AskResponse, AskResponseKind } from "../types/ask";
import { useStoreId } from "../../../core/auth/useLoggedInUser";

const SESSION_KEY = "askFixaChatHistory";

export type ChatMessage = { id: string } & (
  | { role: "user"; text: string }
  | { role: "assistant"; response: AskResponse }
  | { role: "error"; text: string }
  | { role: "too_large" }
  | { role: "timeout" }
  | { role: "empty" }
);

type AskContext = { selectedArticles?: any[] };

type UseAskResult = {
  ask: (question: string, context?: AskContext) => Promise<void>;
  messages: ChatMessage[];
  loading: boolean;
  clearHistory: () => void;
  appendAssistantSummary: (summary: string) => void;
  appendErrorMessage: (text: string) => void;
};

function loadHistory(): ChatMessage[] {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    return stored ? (JSON.parse(stored) as ChatMessage[]) : [];
  } catch {
    return [];
  }
}

function saveHistory(messages: ChatMessage[]): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(messages));
  } catch {
    // sessionStorage not available; silently ignore
  }
}

export function useAsk(): UseAskResult {
  const [messages, setMessages] = useState<ChatMessage[]>(loadHistory);
  const [loading, setLoading] = useState(false);
  const storeId = useStoreId();

  const appendAssistantSummary = (summary: string) => {
    const assistantMessage: ChatMessage = {
      id: `${Date.now()}-assistant-local`,
      role: "assistant",
      response: {
        intent: "task_action",
        sql: "",
        data: [],
        summary,
        tasks: null,
      },
    };

    setMessages((prev) => {
      const updated = [...prev, assistantMessage];
      saveHistory(updated);
      return updated;
    });
  };

  const appendErrorMessage = (text: string) => {
    const errorMessage: ChatMessage = {
      id: `${Date.now()}-error-local`,
      role: "error",
      text,
    };

    setMessages((prev) => {
      const updated = [...prev, errorMessage];
      saveHistory(updated);
      return updated;
    });
  };

  const ask = async (question: string, context?: AskContext) => {
    const userMessage: ChatMessage = {
      id: `${Date.now()}-user`,
      role: "user",
      text: question,
    };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    saveHistory(nextMessages);

    setLoading(true);
    try {
      const response = await askQuestion({
        question,
        storeId,
        selected_articles: context?.selectedArticles,
      });
      const newMessage: ChatMessage = response.kind
        ? {
            id: `${Date.now()}-${response.kind}`,
            role: response.kind as AskResponseKind,
          }
        : response.error
          ? { id: `${Date.now()}-error`, role: "error", text: response.error }
          : { id: `${Date.now()}-assistant`, role: "assistant", response };
      const updated = [...nextMessages, newMessage];
      setMessages(updated);
      saveHistory(updated);
    } catch (err: any) {
      const isTimeout = err.name === "AbortError";
      const newMsg: ChatMessage = isTimeout
        ? { id: `${Date.now()}-timeout`, role: "timeout" }
        : {
            id: `${Date.now()}-error`,
            role: "error",
            text: err.message || "Something went wrong",
          };
      const updated = [...nextMessages, newMsg];
      setMessages(updated);
      saveHistory(updated);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = () => {
    setMessages([]);
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // sessionStorage not available; silently ignore
    }
  };

  return {
    ask,
    messages,
    loading,
    clearHistory,
    appendAssistantSummary,
    appendErrorMessage,
  };
}
